// eslint-disable-next-line @tseslint/no-shadow
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, model, ModelSignal, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { IconModule } from '@ogs/ngx-m3-components';
import { SideMenuTabService } from './components/side-menu-tab/services/side-menu-tab.service';
import { SideMenuTabComponent } from './components/side-menu-tab/side-menu-tab.component';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { DirtyStateService } from '@core/services/dirty-state.service';
import { PendingChangesDialogComponent } from '../dialog/pending-changes/pending-changes-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-side-menu',
  standalone: true,
  imports: [ MatButtonModule, CommonModule, IconModule, NgTemplateOutlet ],
  providers: [ SideMenuTabService ],
  templateUrl: './side-menu.component.html',
  styleUrl: './side-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SideMenuComponent implements AfterViewInit {
  private readonly _tabService: SideMenuTabService = inject(SideMenuTabService);

  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _dirtyStateService: DirtyStateService | null = inject(DirtyStateService, { optional: true });

  private readonly _dialog: MatDialog = inject(MatDialog);

  public readonly isExpanded: ModelSignal<boolean> = model(false);

  public readonly isInitiallyRendered: WritableSignal<boolean> = signal(false);

  // eslint-disable-next-line @unicorn/no-useless-undefined, @tseslint/no-explicit-any
  protected templateToRender: WritableSignal<TemplateRef<any> | undefined> = signal(undefined);

  // eslint-disable-next-line @tseslint/no-explicit-any
  @ViewChild("userGivenTemplate", { "static": true }) private _userGivenTemplate!: TemplateRef<any>;


  constructor () {
    this._faIconLibrary.addIcons(faXmark);
  }

  public ngAfterViewInit (): void {
    this.templateToRender.set(this._userGivenTemplate);
    this.isInitiallyRendered.set(true);
    this._tabService.selectedTab$.subscribe((tab: SideMenuTabComponent | undefined) => {
      if (tab)
        this.templateToRender.set(tab.tabContent);
    });
  }

  /**
   * Closes the menu.
   * if a {@link DirtyStateService} is provided and dirty opens a dialog whether the user wants to discard the changes
   */
  public closeMenu (): void {
    if (this._dirtyStateService?.isDirty()) {
      this._dialog.open(PendingChangesDialogComponent)
        .afterClosed()
        .subscribe((confirmed: boolean): void => {
          if (confirmed) {
            // eslint-disable-next-line @tseslint/no-non-null-assertion
            this._dirtyStateService!.clear();
            this.isExpanded.set(false);
          }
        });
    } else
      this.isExpanded.set(false);
  }
}
